import { FAMILY_GLYPHS } from "./toolGlyphs.jsx";
import { Children, cloneElement, isValidElement } from "react";

// V57.7 — FolderTile renders a clickable family-folder tile for the
// NerdTools 10-folder ToolsHome grid. Visually echoes the NerdSuite
// landing tile pattern (square tile with gradient and a glyph + label).
//
// V57.8 — Glyph swapped from emoji to inline SVG.
// V57.9 — Tile structure matches NerdSuite landing: glyph-only inside,
// name + count below.
//
// V58.1 — Gradient treatment now uses SVG <linearGradient> defs filling
// the tile rect (same construction as SuiteMark for the landing-page
// per-suite tiles). Family accents updated to pastel start hues
// gradiating to a shared rose-magenta end (#A33A93) — the end color
// is between NerdTools master #E0584E (red) and NerdSuite violet
// #7C3AED, giving a magenta-rose that subtly echoes the tools red
// while reading consistent across all 10 families.
//
// Implementation note: FAMILY_GLYPHS stores entries as { __glyph,
// children } where children is React JSX. We embed the children
// directly inside a <g> with transform to position/scale the 24x24
// glyph viewBox into the central 40x40 area of the 100x100 tile
// viewBox. The stroke color is applied via the parent <g>; the glyph
// children inherit it (they use fill="none" stroke="currentColor"
// internally — see g() helper in toolGlyphs.jsx).

// Helper: clone glyph children with white stroke applied. Mirrors
// applyPaint() in toolGlyphs.jsx so the glyph paths render in white
// against the gradient background.
function paintGlyphChildren(node, paint) {
  return Children.map(node, (child) => {
    if (!isValidElement(child)) return child;
    const p = { ...child.props };
    if (p.fill === "GLYPH_PAINT") p.fill = paint;
    if (p.stroke === "GLYPH_PAINT") p.stroke = paint;
    const kids = p.children ? paintGlyphChildren(p.children, paint) : p.children;
    return cloneElement(child, p, kids);
  });
}

export function FolderTile({ family, onClick }) {
  const { id, label, accent, accentDeep, toolCount } = family;
  // Stable gradient id per family — avoids collisions when multiple
  // FolderTiles render on the same page.
  const gradId = `ft-grad-${id}`;
  const glyphEntry = FAMILY_GLYPHS[id];
  // Math glyph uses thicker stroke for π letterform weight; sensible
  // default elsewhere (matches renderFamilyGlyph behavior).
  const glyphStrokeWidth = id === "math-engineering" ? 2.2 : 1.8;

  return (
    <button
      onClick={onClick}
      aria-label={`Open ${label} folder (${toolCount} tools)`}
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
      }}
    >
      {/* SVG-rendered tile — gradient fill + glyph overlay */}
      <div
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          borderRadius: 22,
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 8px 18px rgba(0,0,0,0.35)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)";
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: "block" }}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor={accent} />
              <stop offset="1" stopColor={accentDeep} />
            </linearGradient>
          </defs>
          {/* Gradient-filled rounded square */}
          <rect width="100" height="100" rx="22" ry="22" fill={`url(#${gradId})`} />
          {/* Glyph centered on the tile.
              The glyph's source viewBox is 24x24. We want it to occupy
              ~40x40 of the 100x100 tile (centered), so:
                scale = 40 / 24 ≈ 1.667
                translate = (100 - 40) / 2 = 30
              Stroke width is set on the parent <g> so it propagates to
              all path/circle children that use stroke="currentColor".
          */}
          {glyphEntry && (
            <g
              transform="translate(30, 30) scale(1.667)"
              fill="none"
              stroke="#fff"
              strokeWidth={glyphStrokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {paintGlyphChildren(glyphEntry.children, "#fff")}
            </g>
          )}
        </svg>
      </div>
      {/* Caption — family name + tool count */}
      <div style={{ width: "100%", textAlign: "center", paddingTop: 2 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#E8E9F3",
          lineHeight: 1.2,
          letterSpacing: "-0.2px",
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 10,
          fontWeight: 500,
          color: "#7C8099",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          marginTop: 2,
        }}>
          {toolCount} {toolCount === 1 ? "tool" : "tools"}
        </div>
      </div>
    </button>
  );
}
